/* ============================================================
   VEYA - STICKY ADD TO CART

   A sentinel is inserted directly after the real buy buttons and
   watched with an IntersectionObserver. When it leaves the top of
   the viewport the bar slides in; when it comes back the bar
   leaves. No scroll listener, so nothing runs between frames and
   nothing reads layout on the scroll path.

   THE DIRECTION TEST
   isIntersecting alone is not enough. A sentinel below the fold
   on first paint is also "not intersecting", so a naive check
   shows the bar the instant the page loads - before the shopper
   has seen the buy box at all. boundingClientRect.top < 0 is what
   separates "scrolled past it" from "not reached it yet".

   THE BAR OWNS NO FORM
   It forwards its click to the page's real submit button. That
   keeps variant selection, quantity rules, sold-out state, error
   messages and the cart drawer in exactly one place. The theme's
   own sticky cart took the other route - a duplicated form kept
   in sync by product-info.js - which is why it needs machinery to
   mirror quantity between two inputs.
   ============================================================ */

class VeyaStickyAtc extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('[data-veya-atc-btn]');
    this.labelEl = this.querySelector('[data-veya-atc-label]');
    this.priceEl = this.querySelector('[data-veya-atc-price]');
    this.metaEl = this.querySelector('[data-veya-atc-meta]');

    this.ctaText = (this.dataset.cta || 'Add to Cart').trim();
    this.soldOutText = (this.dataset.soldOut || 'Sold out').trim();
    this.defaultMeta = this.metaEl ? this.metaEl.textContent.trim() : '';

    try {
      const raw = this.querySelector('[data-veya-atc-variants]');
      this.variants = raw ? JSON.parse(raw.textContent) : {};
    } catch (e) {
      this.variants = {};
    }

    // Without a real button there is nothing to proxy, so the bar
    // stays where it is: hidden, inert, and out of the way.
    this.realBtn = document.querySelector('[id^="ProductSubmitButton-"]');
    if (!this.realBtn || !this.btn) return;

    this.infoContainer = this.realBtn.closest('.product__info-container') || document;

    this.mountSentinel();
    this.watchSentinel();
    this.watchAvailability();
    this.watchVariant();
    this.syncAvailability();
    this.syncVariant();

    this.btn.addEventListener('click', this.forward.bind(this));
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.availabilityObserver?.disconnect();
    this.variantObserver?.disconnect();
    this.sentinel?.remove();
  }

  /* The sentinel goes after the whole button group rather than
     after the button itself, so the bar waits for the dynamic
     checkout buttons to clear too. Otherwise it would appear while
     Apple Pay is still on screen, which reads as a duplicate. */
  mountSentinel() {
    const anchor = this.realBtn.closest('.product-form__buttons') || this.realBtn;
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'veya-atc-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    anchor.insertAdjacentElement('afterend', this.sentinel);
  }

  watchSentinel() {
    if (!('IntersectionObserver' in window)) return; // bar stays hidden
    this.io = new IntersectionObserver(
      (entries) => this.handleEntry(entries[entries.length - 1]),
      { threshold: 0 }
    );
    this.io.observe(this.sentinel);
  }

  /* Kept as its own method rather than inlined in the observer
     callback so the decision can be exercised directly with a
     synthetic entry. An IntersectionObserver delivers nothing at
     all while document.visibilityState is "hidden", which makes
     the rule below untestable in any headless or backgrounded
     browser unless it can be called on its own. */
  handleEntry(entry) {
    if (!entry) return;
    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    this.setVisible(scrolledPast);
  }

  setVisible(on) {
    if (on === this.visible) return;
    this.visible = on;
    this.toggleAttribute('data-visible', on);
    // Reserve the space only while the bar is actually there, so
    // the end of the page is reachable rather than sitting under it.
    document.documentElement.style.setProperty(
      '--veya-atc-height',
      on ? `${Math.round(this.getBoundingClientRect().height)}px` : '0px'
    );
  }

  /* Sold out, unavailable, or a quantity rule the shopper cannot
     satisfy - all of it surfaces as [disabled] on the real button,
     so mirroring that one attribute covers every case without this
     file needing to know why. */
  watchAvailability() {
    this.availabilityObserver = new MutationObserver(this.syncAvailability.bind(this));
    this.availabilityObserver.observe(this.realBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  syncAvailability() {
    const disabled = this.realBtn.hasAttribute('disabled');
    this.btn.disabled = disabled;
    if (this.labelEl) this.labelEl.textContent = disabled ? this.soldOutText : this.ctaText;
  }

  /* The theme swaps the whole info container on a variant change
     rather than mutating fields in place, so the hidden input is a
     different node afterwards. Watching the subtree and re-reading
     the value is therefore more reliable than holding a reference
     to any single element. */
  watchVariant() {
    if (this.infoContainer === document) return;
    this.variantObserver = new MutationObserver(() => {
      clearTimeout(this.variantTimer);
      this.variantTimer = setTimeout(() => {
        // The button is replaced along with everything else.
        const fresh = document.querySelector('[id^="ProductSubmitButton-"]');
        if (fresh && fresh !== this.realBtn) {
          this.realBtn = fresh;
          this.availabilityObserver.disconnect();
          this.watchAvailability();
          if (!document.querySelector('.veya-atc-sentinel')) this.mountSentinel();
        }
        this.syncVariant();
        this.syncAvailability();
      }, 60);
    });
    this.variantObserver.observe(this.infoContainer, { childList: true, subtree: true });
  }

  syncVariant() {
    const input = document.querySelector('.product-variant-id');
    const id = input && input.value;
    const variant = id && this.variants[id];
    if (!variant) return;

    if (this.priceEl && variant.p) this.priceEl.textContent = variant.p;

    /* The default line reads "60 gummies", which is true of the
       single jar and false of every other tier. As soon as a
       different tier is chosen the slot switches to that tier's
       own title, so the bar can never advertise a jar count the
       shopper is not buying. */
    if (this.metaEl) {
      this.metaEl.textContent = variant.d ? this.defaultMeta : variant.t;
    }
  }

  forward(event) {
    event.preventDefault();
    if (this.btn.disabled) return;
    this.realBtn.click();
  }
}

if (!customElements.get('veya-sticky-atc')) {
  customElements.define('veya-sticky-atc', VeyaStickyAtc);
}